import {
	Component,
	ChangeDetectionStrategy,
	ViewChild,
	ElementRef,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	Input,
} from '@angular/core';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import {
	CmmsScheduleMonthChoices,
	CmmsSchedulePeriodsType,
	ICmmsScheduleValue,
} from '@app/pages/cmms/interfaces/scheduled-value.interface';
import { Subject } from 'rxjs';
import { FormBuilder, FormsModule } from '@angular/forms';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';

import { DaysPeriodContentComponent } from './days-period-content/days-period-content.component';
import { WeeksPeriodContentComponent } from './weeks-period-content/weeks-period-content.component';
import { MonthsPeriodContentComponent } from './months-period-content/months-period-content.component';
import { MatInputModule } from '@angular/material/input';
import { NgxTolgeeModule } from '@tolgee/ngx';

@Component({
	selector: 'schedule-options',
	templateUrl: './schedule-options.component.html',
	styleUrls: ['./schedule-options.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatFormFieldModule,
		MatIconModule,
		FormsModule,
		MatSelectModule,
		DaysPeriodContentComponent,
		WeeksPeriodContentComponent,
		MonthsPeriodContentComponent,
		MatInputModule,
		NgxTolgeeModule,
	],
})
export class ScheduleOptionsComponent implements OnInit, OnDestroy {
	@Input() set selectedOptions(value: ICmmsScheduleValue) {
		this.optionsForm.patchValue({ ...value }, { emitEvent: false });
	}

	@Input() selectedDaysError: boolean;
	@Input() useDebounce: boolean = false;

	@Output() optionsChanged = new EventEmitter<Partial<ICmmsScheduleValue>>();

	@ViewChild('repeatNumberInput') repeatNumberInput: ElementRef;

	minRepeatCount = 1;
	maxRepeatCount = 365;

	periods = [
		{
			value: 'DAY',
			name: 'day',
		},
		{
			value: 'WEEK',
			name: 'week',
		},
		{
			value: 'MONTH',
			name: 'month',
		},
		{
			value: 'YEAR',
			name: 'year',
		},
	];

	optionsForm = this.fb.group({
		interval: 1,
		period: 'DAY' as CmmsSchedulePeriodsType,
		excludeWeekends: false,
		selectedDays: [['monday']],
		monthSetup: 'FIRST_DAY' as CmmsScheduleMonthChoices,
	});

	private destroy$ = new Subject<void>();

	constructor(private fb: FormBuilder) {}

	ngOnInit() {
		this.optionsForm.valueChanges
			.pipe(
				takeUntil(this.destroy$),
				// Debounce is just to limit the amount of requests to server due to user changes
				debounceTime(this.useDebounce ? 500 : 0)
			)
			.subscribe(values => {
				this.optionsChanged.emit(values as Partial<ICmmsScheduleValue>);
			});
	}

	numberInputChanged(value) {
		// When we change an input by hands to some values that are not in range
		// we change it by below code to valid ones, but this trigger a form update
		// Also it's ok, if previous value was not one of those default min and max
		// but bad if not. For ex. We had 9 as value and change it to -9, if we don't do the
		// check below and set 'false' directly to setRepeatNumber the formValue will change
		// to 1 but entity update won't be triggered.
		// So we check if interval is not one of default and update, or if it's one of default
		// we don't need to update it again, as for user they are still the same, but in code
		// they have been changed. And yeah, fuck that, I've spent a day trying to resolve it.
		// >_<
		if (!value || value < this.minRepeatCount) {
			// Here because change from 365 to lower than 1  will result in 1, and we need update
			const propagateEvent = this.optionsFormValue.interval !== this.minRepeatCount;
			this.setRepeatNumber(this.minRepeatCount, propagateEvent);
		} else if (value > this.maxRepeatCount) {
			// Here because change from 1 to >365 will result in 365, and we need update
			const propagateEvent = this.optionsFormValue.interval !== this.maxRepeatCount;
			this.setRepeatNumber(this.maxRepeatCount, propagateEvent);
		} else {
			this.setRepeatNumber(value);
		}
	}

	decrementQuantity() {
		if (this.optionsFormValue.interval > 1) {
			this.setRepeatNumber(--this.optionsFormValue.interval);
		}
	}

	incrementQuantity() {
		if (this.optionsFormValue.interval < 365) {
			this.setRepeatNumber(++this.optionsFormValue.interval);
		}
	}

	selectedPeriodChange($event: MatSelectChange) {
		this.optionsForm.patchValue({
			period: $event.value,
			// Below will reset the previously changed values to default
			excludeWeekends: false,
			selectedDays: ['monday'],
			monthSetup: 'FIRST_DAY',
		});
	}

	toggleWeekends(checked: boolean) {
		this.changeFormValue('excludeWeekends', checked);
	}

	changeFormValue(controlName: string, value: any) {
		this.optionsForm.patchValue({
			[controlName]: value,
		});
	}

	private setRepeatNumber(value, propagateFormEvent = true) {
		this.optionsForm.patchValue(
			{
				interval: value,
			},
			{ emitEvent: propagateFormEvent }
		);
		this.repeatNumberInput.nativeElement.value =
			this.optionsFormValue.interval.toString();
	}

	get optionsFormValue(): Partial<ICmmsScheduleValue> {
		return this.optionsForm.value as Partial<ICmmsScheduleValue>;
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
